import '@fontsource-variable/inter';
import '@fontsource-variable/jetbrains-mono';
import {
  MutationCache,
  QueryClient,
  QueryClientProvider,
  type Mutation,
} from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { ConfigProvider } from 'antd';
import { initializeApp } from 'firebase/app';
import {
  browserSessionPersistence,
  connectAuthEmulator,
  getAdditionalUserInfo,
  getAuth,
  isSignInWithEmailLink,
  onAuthStateChanged,
  setPersistence,
  signInWithEmailLink,
  User as FirebaseUser,
} from 'firebase/auth';
import { connectFirestoreEmulator, getFirestore } from 'firebase/firestore';
import Gleap from 'gleap';
import { X } from 'lucide-react';
import { StrictMode, useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';

import { IMPERSONATION_APP_NAME, getAppAuth } from './api/axios.instance';
import {
  applyOptimisticPatch,
  applyServerResponseToCache,
  rollbackCacheSnapshots,
  type CacheSnapshot,
} from './api/optimisticCache';
import { buildInfo } from './build-info';
import { Error } from './Error';
import { ATTRIBUTION_STORAGE_KEY, captureAttribution } from './modules/attribution';
import { initCrazyEgg, resetCrazyEggIdentity } from './modules/crazy-egg';
import { initDevConsole } from './modules/dev-console';
import {
  identifyGoogleAnalyticsUser,
  initGoogleAnalytics,
  trackGoogleAnalyticsAction,
  trackGoogleAnalyticsPageView,
  trackSignUp,
} from './modules/google-analytics';
import { syncDocumentTitle } from './modules/page-title';
import { resetPayticsIdentity } from './modules/paytics';
import { initPixelFlow, trackPixelFlowPageView } from './modules/pixelflow';
import { clearSentryUser, initSentry, trackAction } from './modules/sentry';
import { NotFound } from './NotFound';
import { routeTree } from './routeTree.gen';
import { darkTheme, lightTheme } from './theme';
import { useThemeMode } from './ui/hooks';
import { setHtmlLoaderText } from './ui/utils/html';
import { storage } from './ui/utils/local-storage';

import './antd-overrides.css';

Gleap.setEnvironment('prod');
Gleap.initialize('bAh3Qk1x9L2TMwq95X4wpIzzxwRCb6PQ');
Gleap.attachCustomData(buildInfo.gleapCustomData);
Gleap.showFeedbackButton(false);

if (import.meta.env.VITE_ENV === 'production') {
  console.log(`App v${buildInfo.version} (${buildInfo.commit})`);
}

// Runs before the router mounts, while the landing URL still carries its query string.
captureAttribution();

initGoogleAnalytics();
initPixelFlow();
initCrazyEgg();
initDevConsole();

// ---------------------------------------------------------------------------
// Firebase configuration
// ---------------------------------------------------------------------------

const firebaseConfig = JSON.parse(import.meta.env.VITE_FIREBASE_WEB_APP_CONFIG_JSON);
initializeApp(firebaseConfig);
const impApp = initializeApp(firebaseConfig, IMPERSONATION_APP_NAME);
setPersistence(getAuth(impApp), browserSessionPersistence);

if (import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_HOST) {
  connectAuthEmulator(getAuth(), import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_HOST, {
    disableWarnings: true,
  });
  connectAuthEmulator(getAuth(impApp), import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_HOST, {
    disableWarnings: true,
  });
}

if (import.meta.env.VITE_FIRESTORE_EMULATOR_HOST) {
  const [host, port] = import.meta.env.VITE_FIRESTORE_EMULATOR_HOST.split(':');
  connectFirestoreEmulator(getFirestore(), host, parseInt(port));
}

const EMAIL_FOR_SIGN_IN_STORAGE_KEY = 'emailForSignIn';

const completeEmailLinkSignIn = async (): Promise<void> => {
  const auth = getAuth();

  if (!isSignInWithEmailLink(auth, window.location.href)) {
    return;
  }

  const email = storage.get(EMAIL_FOR_SIGN_IN_STORAGE_KEY);

  if (!email) {
    return;
  }

  setHtmlLoaderText('Signing you in...');

  try {
    const credential = await signInWithEmailLink(auth, email, window.location.href);

    storage.remove(EMAIL_FOR_SIGN_IN_STORAGE_KEY);

    if (getAdditionalUserInfo(credential)?.isNewUser) {
      trackSignUp('email');
    }

    const redirectTo = new URL(window.location.href).searchParams.get('redirectTo');
    const target =
      redirectTo?.startsWith('/') && !redirectTo.startsWith('//') && !redirectTo.startsWith('/\\')
        ? redirectTo
        : '/';

    window.history.replaceState(null, '', target);
  } catch {
    void 0;
  }
};

// ---------------------------------------------------------------------------
// Query client
// ---------------------------------------------------------------------------

const snapshotsByMutation = new WeakMap<Mutation<unknown, unknown, unknown>, CacheSnapshot[]>();
const mutationCache: MutationCache = new MutationCache({
  onMutate: (variables, mutation) =>
    snapshotsByMutation.set(mutation, applyOptimisticPatch(queryClient, variables, mutation.meta)),
  onSuccess: (data, variables, _onMutateResult, mutation) => {
    applyServerResponseToCache(queryClient, variables, data, mutation.meta);

    if (mutation.meta?.track) {
      trackAction(mutation.meta.track);
      trackGoogleAnalyticsAction(mutation.meta.track);
    }
  },
  onError: (_error, _variables, onMutateResult, mutation) => {
    rollbackCacheSnapshots(queryClient, onMutateResult);
    rollbackCacheSnapshots(queryClient, snapshotsByMutation.get(mutation));
  },
  onSettled: (_data, _error, _variables, _onMutateResult, mutation) => {
    snapshotsByMutation.delete(mutation);

    for (const fragment of mutation.meta?.invalidate ?? []) {
      queryClient.invalidateQueries({
        predicate: (query) => String(query.queryKey[0]).includes(fragment),
      });
    }
  },
});

const queryClient = new QueryClient({
  mutationCache,
  defaultOptions: {
    queries: {
      retry: false, // 99.99% of the time, it's not worth it
      refetchOnMount: true,
      refetchOnWindowFocus: true,
    },
    mutations: {
      retry: false, // Not all endpoints are idempotent, so we don't want to retry them
      networkMode: 'always', // Offline must fail the mutation
    },
  },
});

// ---------------------------------------------------------------------------
// Router
// ---------------------------------------------------------------------------

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  scrollRestoration: true,
  defaultErrorComponent: Error,
  defaultNotFoundComponent: NotFound,
  context: {
    queryClient,
  },
});

// Sentry needs the router for route-named navigation traces, so it starts here, not at import.
initSentry(router);

// Tab title per route in every environment; the GA page view is a no-op outside production.
router.subscribe('onResolved', () => {
  trackGoogleAnalyticsPageView(syncDocumentTitle(router));
});

if (import.meta.env.VITE_ENV === 'production') {
  let lastTrackedHref = window.location.pathname + window.location.search;

  router.subscribe('onResolved', () => {
    const href = window.location.pathname + window.location.search;

    if (href === lastTrackedHref) {
      return;
    }

    lastTrackedHref = href;
    trackPixelFlowPageView();
  });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------

const Root = () => {
  const [isAuthStateResolved, setIsAuthStateResolved] = useState(false);
  const { mode } = useThemeMode();

  const isFirstAuthStateChangeRef = useRef(true);
  const firebaseUserRef = useRef<FirebaseUser | null>(null);

  useEffect(() => {
    setHtmlLoaderText('Checking authentication...');

    let unsubscribe: (() => void) | null = null;
    let cancelled = false;

    completeEmailLinkSignIn().then(() => {
      if (cancelled) {
        return;
      }

      unsubscribe = onAuthStateChanged(getAppAuth(), (user) => {
        if (isFirstAuthStateChangeRef.current) {
          isFirstAuthStateChangeRef.current = false;
          firebaseUserRef.current = user;

          setIsAuthStateResolved(true);
          return;
        }

        if (firebaseUserRef.current?.uid !== user?.uid) {
          console.log('invalidating router because the user is different from the previous one');
          queryClient.clear();
          router.invalidate();
        }

        if (!user && firebaseUserRef.current) {
          storage.remove(ATTRIBUTION_STORAGE_KEY);
        }

        if (!user) {
          Gleap.clearIdentity();
        }

        if (!user) {
          clearSentryUser();
          identifyGoogleAnalyticsUser(null);
          resetPayticsIdentity();
        }

        if (!user && import.meta.env.VITE_ENV === 'production') {
          resetCrazyEggIdentity();
        }

        firebaseUserRef.current = user;
      });
    });

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, []);

  useEffect(() => {
    const handleAccessDisabled = () => {
      router.navigate({ to: '/auth/access-disabled' });
    };

    window.addEventListener('auth:access_disabled', handleAccessDisabled);

    return () => {
      window.removeEventListener('auth:access_disabled', handleAccessDisabled);
    };
  }, []);

  if (!isAuthStateResolved) {
    return null;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ConfigProvider
        theme={mode === 'dark' ? darkTheme : lightTheme}
        wave={{ disabled: true }}
        button={{ shape: 'round' }}
        drawer={{
          closable: { placement: 'end' },
          closeIcon: <X size={15} />,
          styles: ({ props }) => ({
            wrapper: {
              inset: props.placement === 'left' ? '10px auto 10px 10px' : '10px 10px 10px auto',
              height: 'calc(100dvh - 20px)',
              maxWidth: 'calc(100vw - 20px)',
              boxShadow: 'none',
            },
            section: {
              border: '1px solid var(--ant-color-border-secondary)',
              borderRadius: 20,
              overflow: 'hidden',
              boxShadow: 'var(--ant-box-shadow-secondary)',
            },
            close: { width: 30, height: 30, color: 'var(--ant-color-text-tertiary)' },
          }),
        }}
        tooltip={window.matchMedia('(hover: none)').matches ? { trigger: [] } : undefined}
        popover={{ arrow: false }}
        tag={{ style: { lineHeight: 'normal', borderRadius: 999 } }}
        segmented={{
          styles: {
            root: {
              padding: '3px 2px',
              background: 'var(--ant-color-fill-tertiary)',
              boxShadow: 'inset 0 1px 3px var(--ant-color-fill), inset 0 0 1px var(--ant-color-fill)',
            },
            item: { marginInline: 1 },
            icon: { display: 'inline-flex' },
            label: {
              minHeight: 25,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              paddingInline: 10,
            },
          },
        }}
        popconfirm={{ arrow: false }}
      >
        <RouterProvider router={router} context={{ queryClient }} />
      </ConfigProvider>
    </QueryClientProvider>
  );
};

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Root />
  </StrictMode>,
);
